import { useTheme } from 'styled-components'

import { FormatNumber, Text } from '@reveni-io/reveni-ui'

import NoResultsFound from '@/common/components/NoResultsFound/NoResultsFound'
import { orderByValues } from '@/metrics/hooks/useFilters/constants'

import ReasonsBreakdownTableSkeleton from './ReasonsBreakdownTableSkeleton/ReasonsBreakdownTableSkeleton'
import TableHeader from './TableHeader/TableHeader'
import * as Styles from './styles'

const ReasonsBreakdownTable = ({
  nameLabel,
  reasons,
  loading,
  showExchanges,
  onSort,
  orderBy,
  onClickReason,
  isRowClickable,
}) => {
  const theme = useTheme()

  const isSortedBy = field => orderBy?.replace('-', '') === field

  const getCountTextType = field => (isSortedBy(field) ? 'titleS' : 'bodyMRegular')

  const getCountTextColor = field => (isSortedBy(field) ? theme.colors.text.default : theme.colors.text.subtle)

  const canClickRow = reason => !!onClickReason && (!isRowClickable || isRowClickable(reason))

  const handleClickRow = reason => {
    if (!canClickRow(reason)) return

    onClickReason(reason.id)
  }

  const handleRowKeyDown = (event, reason) => {
    if (!canClickRow(reason)) return
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    onClickReason(reason.id)
  }

  if (loading) return <ReasonsBreakdownTableSkeleton />
  if (!reasons?.length) return <NoResultsFound />

  return (
    <Styles.Container>
      <Styles.Table>
        <TableHeader nameLabel={nameLabel} showExchanges={showExchanges} onSort={onSort} orderBy={orderBy} />
        <tbody>
          {reasons.map((reason, index) => (
            <Styles.Row
              key={reason.id ?? index}
              clickable={canClickRow(reason)}
              tabIndex={canClickRow(reason) ? 0 : undefined}
              onClick={() => handleClickRow(reason)}
              onKeyDown={event => handleRowKeyDown(event, reason)}
            >
              <Styles.IndexCell>
                <Text type="bodyMRegular">{index + 1}</Text>
              </Styles.IndexCell>
              <Styles.Cell>
                <Text type="titleS">{reason.display}</Text>
              </Styles.Cell>
              <Styles.NumericCell>
                <Text
                  type={getCountTextType(orderByValues.refundCount)}
                  color={getCountTextColor(orderByValues.refundCount)}
                >
                  <FormatNumber style="decimal">{reason.refund_count}</FormatNumber>
                </Text>
              </Styles.NumericCell>
              {showExchanges && (
                <>
                  <Styles.NumericCell>
                    <Text
                      type={getCountTextType(orderByValues.exchangeCount)}
                      color={getCountTextColor(orderByValues.exchangeCount)}
                    >
                      <FormatNumber style="decimal">{reason.exchange_count}</FormatNumber>
                    </Text>
                  </Styles.NumericCell>
                  <Styles.NumericCell>
                    <Text type={getCountTextType(orderByValues.count)} color={getCountTextColor(orderByValues.count)}>
                      <FormatNumber style="decimal">{reason.count}</FormatNumber>
                    </Text>
                  </Styles.NumericCell>
                </>
              )}
            </Styles.Row>
          ))}
        </tbody>
      </Styles.Table>
    </Styles.Container>
  )
}

export default ReasonsBreakdownTable
